import { municipalityPageTabToUrlSegment } from '@app/pages/discover/regions/parseMunicipalityPageTab'
import { regionPageTabToUrlSegment } from '@app/pages/discover/regions/parseRegionPageTab'
import { MUNICIPALITY_FEATURES, REGION_FEATURES } from '@schema/featureToggle'

export const pages = {
  home: '/',
  login: '/login',
  signup: '/signup',
  profile: (id: string) => `/profielen/${id}`,
  discover: '/d',
  discussions: '/prikbord',
  initiatives: '/d/initiatieven',
  initiativesWithApplyPhase: (phaseType: string) =>
    `/d/initiatieven?applyPhase=${phaseType}`,
  initiative: (slug: string) => `/d/initiatieven/${slug}`,
  plots: '/d/locaties',
  plot: (id: string) => `/d/locaties/${id}`,
  regions: '/d/regios',
  regionPage: (id: string) => `/d/regios/provincie/${id}`,
  regionPageNew: (slug: string, tab?: string) => {
    if (!tab || tab === REGION_FEATURES.overview) {
      return `/provincie/${slug}`
    }
    return `/provincie/${slug}/${regionPageTabToUrlSegment(tab)}`
  },
  regionArticle: (slug: string, blogType: string, articleSlug: string) =>
    `/provincie/${slug}/actueel/${blogType}/${articleSlug}`,
  regionKnowledgeHubModule: (regionSlug: string, moduleSlug: string) =>
    `/provincie/${regionSlug}/${regionPageTabToUrlSegment(REGION_FEATURES.knowledgeHub)}/${moduleSlug}`,
  municipalityPage: (id: string) => `/d/regios/gemeente/${id}`,
  municipalityPageNew: (slug: string, tab?: string) => {
    if (!tab || tab === MUNICIPALITY_FEATURES.overview) {
      return `/gemeente/${slug}`
    }
    return `/gemeente/${slug}/${municipalityPageTabToUrlSegment(tab)}`
  },
  municipalityArticle: (slug: string, blogType: string, articleSlug: string) =>
    `/gemeente/${slug}/actueel/${blogType}/${articleSlug}`,
  municipalityKnowledgeHubModule: (
    municipalitySlug: string,
    moduleSlug: string,
  ) =>
    `/gemeente/${municipalitySlug}/${municipalityPageTabToUrlSegment(MUNICIPALITY_FEATURES.knowledgeHub)}/${moduleSlug}`,
  regionalBusinessProfile: (id: string) => `/d/regios/profielen/${id}`,
  housingForms: '/d/woonvormen',
  housingForm: (id: string) => `/d/woonvormen/${id}`,
  topicCommunity: (id: string) => `/d/organisaties/${id}`,
  serviceProviders: '/d/experts',
  serviceProvider: (id: string) => `/d/experts/${id}`,
  serviceProviderConcept: (id: string) => `/d/experts/concepts/${id}`,
  blogs: (blogType?: string) =>
    blogType ? `/actueel/${blogType}` : '/actueel',
  blog: (blogType: string, id: string) => `/actueel/${blogType}/${id}`,
  aboutUs: '/over-crowdbuilding',
  legal: '/juridisch',
  stewardOwnership: '/steward-ownership',
  contactUs: '/contact',
  forExperts: '/voor-experts',
  forMunicipalities: '/voor-gemeenten',
  events: '/agenda',
  event: (slug: string) => `/agenda/${slug}`,
  eventCreateStep: (id: string, step: string) => `/agenda/create/${id}/${step}`,
  knowledgeHub: '/kennishub',
  knowledgeHubModule: (moduleSlug: string) => `/kennishub/${moduleSlug}`,
  elearning: '/leer-omgeving',
  elearningItem: (moduleSlug: string) => `/leer-omgeving/${moduleSlug}`,
  dashboard: '/account',
  dashboardMessages: '/account/berichten',
  dashboardViewConversation: (id: string) => `/account/berichten/${id}/view`,
  dashboardInitiatives: '/account/initiatieven',
  dashboardViewInitiative: (id: string) => `/account/initiatieven/${id}/view`,
  dashboardManagePeopleInitiative: (id: string) =>
    `/account/initiatieven/${id}/mensen`,
  dashboardInitiativeEvents: (id: string) =>
    `/account/initiatieven/${id}/events`,
  dashboardCreateEvent: (id: string) =>
    `/account/initiatieven/${id}/events/create`,
  dashboardStartInitiative: '/account/initiatieven/start',
  dashboardStartInitiativeMenu: '/account/initiatieven/start/menu',
  dashboardStartInitiativeLiving: '/account/initiatieven/start/living',
  dashboardOnboardInitiative: (id: string, step: string) =>
    `/account/initiatieven/${id}/onboard/${step}`,
  dashboardProfile: '/account/profiel',
  dashboardPreferences: '/account/voorkeuren',
  dashboardFollowing: '/account/favorieten',
  rabobankLanding: '/rabobank-cpo-desk',
  plotRegistry: '/processes/plot-registry',
}

export type GeographyPage = 'region' | 'municipality'

export const geographyArticleLink = (
  geographyPage: GeographyPage | undefined,
  slug: string,
  blogType: string,
  articleSlug: string,
): string => {
  if (slug && geographyPage === 'region') {
    return pages.regionArticle(slug, blogType, articleSlug)
  }
  if (slug && geographyPage === 'municipality') {
    return pages.municipalityArticle(slug, blogType, articleSlug)
  }
  return pages.blog(blogType, articleSlug)
}

const PROD_DOMAIN = 'crowdbuilding.com'
const DEV_DOMAIN = 'dev-crowdbuilding.based.dev'
const DEV_TEST_DOMAIN = 'dev-test-crowdbuilding.based.dev'

export const getFullRootDomain = (processEnv: string) =>
  `https://${getRootDomain(processEnv)}`

export const getRootDomain = (processEnv?: string) => {
  if (processEnv === 'production') return PROD_DOMAIN
  if (processEnv === 'development') return DEV_DOMAIN
  if (processEnv === 'dev-test') return DEV_TEST_DOMAIN

  if (!processEnv) return 'localhost:1234'

  return 'UNKNOWN_ENVIRONMENT'
}

const INTERNAL_HOSTS = new Set([PROD_DOMAIN, DEV_DOMAIN, DEV_TEST_DOMAIN])

export const isInternalHost = (host: string): boolean => {
  if (host === 'localhost' || host.startsWith('localhost:')) {
    return true
  }

  return INTERNAL_HOSTS.has(host)
}

export const social = {
  facebook: 'https://www.facebook.com/Crowdbuilding',
  instagram: 'https://www.instagram.com/crowd.building',
  linkedin: 'https://www.linkedin.com/company/crowdbuilding',
  tiktok: 'https://www.tiktok.com/@crowdbuilding',
}

// export const externalPages = {
//   jobs: 'https://crowdbuilding.recruitee.com/',
// }

export type RedirectRule = {
  from: string
  to: string | ((params: Record<string, string>) => string)
  external?: boolean
}

export const redirects: RedirectRule[] = [
  { from: '/voor-grondeigenaren', to: pages.forExperts },
  { from: '/experts', to: pages.serviceProviders },
  { from: '/prikbord-new', to: pages.initiatives },
  { from: '/overheidsloket', to: pages.regions },
  { from: '/algemene-voorwaarden', to: pages.legal },
  { from: '/privacy-policy', to: pages.legal },
  { from: '/woonvormen', to: pages.housingForms },
  { from: '/discover', to: pages.initiatives },
  { from: '/kavels', to: pages.plots },
  { from: '/discover-kavels', to: pages.plots },
  { from: '/cookies', to: pages.legal },
  {
    from: '/pages/gemeente-:name',
    to: (params) => pages.municipalityPage(`gemeente-${params.name}`),
  },
  { from: '/blog/:slug', to: (params) => pages.blog('blog', params.slug) },
  { from: '/blog', to: pages.blogs('blog') },
  { from: '/groups/:id', to: (params) => pages.initiative(params.id) },
  { from: '/plots/:id', to: (params) => pages.plot(params.id) },
  { from: '/woonvormen/:id', to: (params) => pages.housingForm(params.id) },
  { from: '/kennishub/:first/:rest*', to: pages.knowledgeHub },
]

/**
 * Matches a pathname against the redirect rules and returns the destination.
 * Supports both named params (:param) and wildcard params (:param*).
 */
export function matchRedirect(
  pathname: string,
): { to: string; external?: boolean } | null {
  for (const { from, to, external } of redirects) {
    const paramNames: string[] = []
    const regexStr = from
      .replace(/:(\w+)\*/g, (_, name) => {
        paramNames.push(name)
        return '(.*)'
      })
      .replace(/:(\w+)/g, (_, name) => {
        paramNames.push(name)
        return '([^/]+)'
      })
    const m = pathname.match(new RegExp(`^${regexStr}$`))
    if (!m) {
      continue
    }

    const params: Record<string, string> = {}
    paramNames.forEach((name, i) => {
      params[name] = m[i + 1]
    })
    return { to: typeof to === 'function' ? to(params) : to, external }
  }
  return null
}
